<script>
import throttle from 'lodash/throttle';
import ResizeObserver from 'resize-observer-polyfill';
import { h } from 'vue';

/**
 * This is a wrapper component that determines the size of a wrapper component. This can be used for localized breakpoints.
 * think of this as window.resize for elements.
 */
export default {
  name: 'SbFlexWrapped',
  props: {
    /**
     * This is the element type that is the wrapper element for whatever you want flex wrapped.
     */
    element: {
      type: String,
      default: 'div',
    },
    /**
     * Classname to be applied to the wrapper
     * Default: 'sb-flex-wrapper'
     */
    className: {
      type: String,
      default: 'sb-flex-wrapped',
    },
    /**
    * Depth of the tree to be analysed for wrapping
    */
    depth: {
      type: Number,
      default: 1,
    },
    /**
    * Threshold beyond which we want the element to wrap
    */
    threshold: {
      type: Number,
      default: 0,
    },
    /* disable resize */
    disabled: {
      type: Boolean,
    },
  },
  data: () => ({
    wrapped: undefined,
    observer: null,
  }),
  mounted() {
    if (this.disabled) return;
    this.$nextTick(() => {
      const handleResize = throttle((entries) => {
        const cr = entries[0].contentRect;
        const { children } = entries[0].target;
        const targetElements = [...children];
        // eslint-disable-next-line no-plusplus
        for (let i = 1; i < this.depth; i++) {
          // This was throwing an error so changed it to reduce instead
          // targetElements = targetElements.flatMap((el) => [...el.children]);
          targetElements.reduce((acc, el) => acc.concat([...el.children]), []);
        }
        let width = 0;
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < targetElements.length; i++) {
          const child = targetElements[i];
          const computedStyle = this.getComputedStyle(child);
          const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
          const marginRight = parseFloat(computedStyle.marginRight) || 0;
          width += child.offsetWidth + marginLeft + marginRight;
        }
        if (this.wrapped) width += this.threshold;
        this.wrapped = Math.ceil(cr.width) < width;
      }, 500);

      this.observer = new ResizeObserver(handleResize);
      if (this.$el instanceof Element) this.observer.observe(this.$el);
    });
  },
  unmounted() {
    this.observer?.disconnect();
  },
  methods: {
    getComputedStyle(el) {
      if (document.defaultView && document.defaultView.getComputedStyle) {
        return document.defaultView.getComputedStyle(el);
      }
      return window.getComputedStyle(el);
    },
  },
  render() {
    const defaultSlots = this.$slots.default();
    const { wrapped } = this;
    const configuredSlots = defaultSlots.reduce((acc, s) => {
      if (s.elm && s.elm.classList) {
        const { classList } = s.elm;
        if (wrapped) {
          classList.add(this.className);
        } else {
          classList.remove(this.className);
        }
      }
      acc.push(s);
      return acc;
    }, []);
    return h(
      this.element,
      {
        class: {
          'sb-flex-wrapped-container': this.wrapped,
        },
        style: {
          display: 'flex',
          flexWrap: 'wrap',
        },
      },
      configuredSlots,
    );
  },
};
</script>
